import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getRoomHosts, setRoomHostStatus } from '../api/admin';
import { useCountry } from '../context/CountryContext';
import UserDetailModal from '../components/UserDetailModal';

const ROLE_LABEL = {
  hotel_host: 'Hotel / Vacation Rental Host',
  event_host: 'Wedding Hall / Event Host',
  vehicle_owner: 'Vehicle Owner (earlier Rooms setup)',
  private_person: 'Customer (earlier Rooms setup)',
};

const STATUS_FILTERS = [
  { key: '', label: 'All statuses' },
  { key: 'verified', label: 'Verified' },
  { key: 'pending', label: 'Pending' },
  { key: 'blocked', label: 'Blocked' },
];

const STATUS_STYLE = {
  verified: { background: '#dcfce7', color: '#166534' },
  pending: { background: '#fef9c3', color: '#854d0e' },
  blocked: { background: '#fee2e2', color: '#991b1b' },
};

// Accommodation/Providers › Host Accounts — status of each host account
// (verify / block). Kayd, 19.10.2026: the list follows the country selected
// in the header (a host belongs to their account's country; the server
// enforces it, also for restricted staff) and shows a clear empty state.
// The status filter lives in the URL, so it survives a page refresh.
const AccommodationProviderApplications = () => {
  const { country, availableOptions, ready: countryReady } = useCountry();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = STATUS_FILTERS.some((f) => f.key === searchParams.get('status')) ? searchParams.get('status') : '';
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [blockingId, setBlockingId] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [openUserId, setOpenUserId] = useState(null);
  const requestSeq = useRef(0);

  const load = () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    const params = {};
    if (country && country !== 'ALL' && country !== 'NONE') params.country = country;
    if (statusFilter) params.kyc_status = statusFilter;
    getRoomHosts(params)
      .then((res) => { if (seq === requestSeq.current) { setHosts(res.data?.hosts || []); setError(''); } })
      .catch((e) => { if (seq === requestSeq.current) { setHosts([]); setError(e.response?.data?.error || 'Could not load host accounts.'); } })
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
  };
  useEffect(() => { if (countryReady) load(); }, [country, statusFilter, countryReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatusFilter = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set('status', v); else next.delete('status');
    setSearchParams(next, { replace: true });
  };

  const act = async (id, kyc_status, blocked_reason) => {
    try {
      await setRoomHostStatus(id, kyc_status, blocked_reason);
      toast.success(kyc_status === 'blocked' ? 'Host blocked — their listings are suspended' : `Host ${kyc_status}`);
      setBlockingId(null); setBlockReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Action failed');
    }
  };

  const countryName = country === 'ALL' ? 'all countries' : (availableOptions.find((o) => o.code === country)?.label || country);
  const statusName = STATUS_FILTERS.find((f) => f.key === statusFilter)?.label.toLowerCase();

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Host Accounts</h1>
      <p style={s.sub}>
        Status of each host account. Blocking a host suspends all of their listings. Open an account to edit or delete
        it and to set its fees &amp; agreements. New host applications and their documents are reviewed under Host Applications.
      </p>

      <div style={s.filters}>
        <select style={s.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status">
          {STATUS_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      </div>

      <p style={s.summary} aria-live="polite">
        {loading ? 'Loading…' : `${hosts.length} host account${hosts.length === 1 ? '' : 's'}${statusFilter ? ` (${statusName})` : ''} · ${countryName}`}
      </p>
      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

      {!loading && !error && hosts.length === 0 && (
        <div style={s.empty}>
          No {statusFilter ? `${statusName} ` : ''}host accounts in {countryName}.
          {country !== 'ALL' && <div style={s.emptyHint}>Choose "All countries" or another country in the header to see more.</div>}
        </div>
      )}

      {!loading && hosts.length > 0 && (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Provider</th><th style={s.th}>Account type</th><th style={s.th}>Country</th>
              <th style={s.th}>Status</th><th style={s.th}>Actions</th>
            </tr></thead>
            <tbody>
              {hosts.map((h) => (
                <tr key={h.id}>
                  <td style={s.td}>
                    <div style={{ fontWeight: 600 }}>{h.user?.full_name || h.user?.email}</div>
                    <div style={s.muted}>{h.user?.customer_number ? `#${h.user.customer_number} · ` : ''}{h.user?.email}</div>
                  </td>
                  <td style={s.td}>{ROLE_LABEL[h.user?.user_role] || h.user?.user_role || '—'}</td>
                  <td style={s.td}>{h.user?.country_code || '—'}</td>
                  <td style={s.td}><span style={{ ...s.badge, ...(STATUS_STYLE[h.kyc_status] || {}) }}>{h.kyc_status}</span></td>
                  <td style={s.td}>
                    {h.user?.id && <button style={s.btnGray} onClick={() => setOpenUserId(h.user.id)}>Open account</button>}
                    {h.kyc_status !== 'verified' && <button style={s.btnGreen} onClick={() => act(h.id, 'verified')}>Verify</button>}
                    {h.kyc_status !== 'blocked' ? (
                      blockingId === h.id ? (
                        <span style={s.inlineForm}>
                          <input style={s.smallInput} placeholder="Reason" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} aria-label="Block reason" />
                          <button style={s.btnRed} onClick={() => act(h.id, 'blocked', blockReason)}>Confirm block</button>
                          <button style={s.btnGray} onClick={() => setBlockingId(null)}>Cancel</button>
                        </span>
                      ) : <button style={s.btnRed} onClick={() => setBlockingId(h.id)}>Block</button>
                    ) : <button style={s.btnGray} onClick={() => act(h.id, 'pending')}>Unblock</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openUserId && (
        <UserDetailModal userId={openUserId} onClose={() => setOpenUserId(null)} onChanged={load} onDeleted={load} />
      )}
    </div>
  );
};

const s = {
  page: { padding: 24, maxWidth: 1000 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 6 },
  sub: { color: '#6b7280', fontSize: 13, margin: '0 0 14px', maxWidth: 700 },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  select: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 },
  summary: { fontSize: 13, color: '#475569', margin: '6px 0 10px', fontWeight: 600 },
  empty: { background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12, padding: '20px 16px', color: '#475569', fontSize: 14, textAlign: 'center' },
  emptyHint: { fontSize: 12.5, color: '#64748b', marginTop: 6 },
  tableWrap: { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  table: { width: '100%', minWidth: 760, borderCollapse: 'collapse', background: 'white', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  muted: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' },
  inlineForm: { display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' },
  btnGreen: { padding: '5px 10px', marginRight: 4, marginBottom: 4, borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnRed: { padding: '5px 10px', marginRight: 4, marginBottom: 4, borderRadius: 6, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnGray: { padding: '5px 10px', marginRight: 4, marginBottom: 4, borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12 },
  smallInput: { padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12, width: 140 },
};

export default AccommodationProviderApplications;
